import { Inject, Injectable } from '@angular/core';
import { AuthResult } from './auth.result';
import { CognitoAuthResponse } from './cognito/cognito-auth.response';
import { WebServiceEndpoints } from '@common/web-service-endpoints';
import { QueryParams, stringifyQueryParams } from '@common/helpers/http-helper';
import { ConfigService } from '@common/services/config/config.service';
import { SpecialistsService } from '@common/services/specialist/specialists.service';
import { AuthApiService, IGetUserTokenDataResponse } from './auth.api.service';
import { TokenService } from '@common/services/utility/token.service';
import { WINDOW } from '@common/services/ui/window.service';
import { CognitoConstants } from './cognitoConstants';
import { JwtHelperService } from '@auth0/angular-jwt';

const authStateItemName = 'auth_state';
const authTokenItemName = 'auth_token';
const accessTokenItemName = 'access_token';
const refreshTokenItemName = 'refresh_token';

@Injectable()
export class AuthService {
  public static anonymousPaths = [
    '/taskrouting/ping',
    '/taskrouting/version',
    '/videochat/ping',
    '/videochat/version',
    '/configuration/ping',
    '/configuration/version',
    '/livechat/ping',
    '/livechat/version',
    '/api/deeplink',
    '/api/token/decrypt',
  ];

  private get appSignInPageUrl(): string {
    return `${window.location.origin}/signin`;
  }

  public get authState(): string {
    return sessionStorage.getItem(authStateItemName);
  }

  public get authToken(): string {
    return sessionStorage.getItem(authTokenItemName);
  }

  public get accessToken(): string {
    return sessionStorage.getItem(accessTokenItemName);
  }

  public get refreshToken(): string {
    return sessionStorage.getItem(refreshTokenItemName);
  }

  constructor(
    private readonly configService: ConfigService,
    private readonly specialistService: SpecialistsService,
    private readonly tokenService: TokenService,
    private readonly authApiService: AuthApiService,
    @Inject(WINDOW) private readonly window: Window,
  ) {}

  public isConfigRequest(requestUrl: string): boolean {
    return requestUrl === WebServiceEndpoints.Settings.config || requestUrl === WebServiceEndpoints.Settings.authorize;
  }

  public isCCTokenRefreshRequest(requestUrl: string): boolean {
    return requestUrl === WebServiceEndpoints.Settings.refreshCCToken;
  }

  public isLogWriteRequest(requestUrl: string): boolean {
    return requestUrl === WebServiceEndpoints.Log.write;
  }

  public isCognitoRequest(requestUrl: string): boolean {
    return requestUrl.match(CognitoConstants.hostUrl) != null;
  }

  public isFiRequest(requestUrl: string): boolean {
    return requestUrl.startsWith(WebServiceEndpoints.apiServiceBaseUrl) && !this.isntNativeMfmPath(requestUrl);
  }

  public isntNativeMfmFiRequest(requestUrl: string): boolean {
    return requestUrl.startsWith(WebServiceEndpoints.apiServiceBaseUrl) && this.isntNativeMfmPath(requestUrl);
  }

  public isSignOutRequest(requestUrl: string): boolean {
    return requestUrl === WebServiceEndpoints.User.globalSignOut;
  }

  public async signedIn(skipRefresh: boolean): Promise<boolean> {
    if (!this.specialistService.specialist) {
      return false;
    }

    if (!this.tokenService.contactCenterToken) {
      return false;
    }

    const cognitoTokenValidateResult = await this.validateAndRefreshCognitoTokens();

    if (cognitoTokenValidateResult === AuthResult.ServerError) {
      this.internalSignOut();
      return false;
    }

    if (skipRefresh) {
      return true;
    }

    if (!this.tokenService.isContactCenterTokenExpired() && this.specialistService.specialist) {
      return true;
    } else {
      let updateTokenResult: AuthResult;

      if (this.isCognitoMode()) {
        updateTokenResult = await this.apiAuthorize(this.authToken);
      } else {
        updateTokenResult = await this.refreshCCToken();
      }

      if (updateTokenResult === AuthResult.ServerError) {
        this.internalSignOut();
      }

      return updateTokenResult === AuthResult.Success;
    }
  }

  private isCognitoMode(): boolean {
    return !!this.authToken;
  }

  private async validateAndRefreshCognitoTokens(): Promise<AuthResult> {
    if (this.authToken) {
      const helper = new JwtHelperService();
      if (helper.isTokenExpired(this.authToken)) {
        return this.refreshCognitoTokens();
      }
    }
    return AuthResult.Success;
  }

  public redirectToAuthPage(): void {
    const authSettings = this.configService.authSettings;

    const queryParams = {
      identity_provider: authSettings.IdentityProvider,
      client_id: authSettings.AppClientId,
      response_type: CognitoConstants.responseType,
      state: this.createAuthState(),
      redirect_uri: this.appSignInPageUrl,
      scope: authSettings.OAuthScopes.join(CognitoConstants.scopeDelimiter),
    };

    const authorizeUrl = this.composeCognitoRedirectUrl(CognitoConstants.endpoints.authorize, queryParams);

    this.redirectTo(authorizeUrl);
  }

  public async signIn(authorizationCode: string): Promise<AuthResult> {
    const body = {
      redirect_uri: this.appSignInPageUrl,
      grant_type: CognitoConstants.grantTypes.authorizationCode,
      code: authorizationCode,
    };

    try {
      const response = await this.requestTokens(body);
      if (response.status === AuthResult.Success) {
        sessionStorage.setItem(authTokenItemName, response.id_token);
        sessionStorage.setItem(accessTokenItemName, response.access_token);
        sessionStorage.setItem(refreshTokenItemName, response.refresh_token);
      } else {
        return AuthResult.ServerError;
      }

      const authResult = await this.apiAuthorize(response.id_token);
      if (authResult !== AuthResult.Success) {
        return AuthResult.ServerError;
      }

      return AuthResult.Success;
    } catch {
      return AuthResult.ServerError;
    }
  }

  public async apiAuthorize(authToken: string): Promise<AuthResult> {
    try {
      const authResponse = await this.authApiService.apiAuthorize(authToken);
      const userTokenData = await this.authApiService.getUserTokenData(authResponse.ContactCenterToken);
      this.initUserAndSaveCCToken(authResponse.ContactCenterToken, null, userTokenData);
      return AuthResult.Success;
    } catch (error) {
      await console.error(error, 'Contact Center authorize error');
      return AuthResult.ServerError;
    }
  }

  public async refreshCCToken(): Promise<AuthResult> {
    try {
      const response = await this.authApiService.refreshCCToken(this.tokenService.contactCenterRefreshToken);
      this.tokenService.setContactCenterToken(response.Token);
      return AuthResult.Success;
    } catch (error) {
      await console.error(error, 'Contact Center refresh token error');
      return AuthResult.ServerError;
    }
  }

  public async refreshCognitoTokens(): Promise<AuthResult> {
    try {
      if (!this.refreshToken) {
        return AuthResult.Success;
      }

      const response = await this.requestTokens({
        grant_type: CognitoConstants.grantTypes.refreshToken,
        refresh_token: this.refreshToken,
      });

      if (response.status !== AuthResult.Success) {
        return AuthResult.ServerError;
      }

      sessionStorage.setItem(authTokenItemName, response.id_token);
      sessionStorage.setItem(accessTokenItemName, response.access_token);

      return AuthResult.Success;
    } catch {
      return AuthResult.ServerError;
    }
  }

  public async apiAuthorizeByCCToken(ccToken: string, ccRefreshToken: string): Promise<AuthResult> {
    try {
      const response = await this.authApiService.getUserTokenData(ccToken);
      this.initUserAndSaveCCToken(ccToken, ccRefreshToken, response);
      return AuthResult.Success;
    } catch (error) {
      await console.error(error, 'Contact Center authorize error');
      return AuthResult.ServerError;
    }
  }

  private initUserAndSaveCCToken(
    ccToken: string,
    ccRefreshToken: string,
    authorizeResponse: IGetUserTokenDataResponse,
  ): void {
    this.tokenService.setContactCenterToken(ccToken);
    this.specialistService.setAndSaveSpecialist({
      Id: authorizeResponse.SpecialistId,
      FirstName: authorizeResponse.FirstName,
      LastName: authorizeResponse.LastName,
      UserName: authorizeResponse.UserName,
      IsAdmin: authorizeResponse.IsAdmin,
      IsEnrolledInDocuSign: authorizeResponse.IsEnrolledInDocuSign,
      DocusignUserId: authorizeResponse.DocusignUserId,
      IsSkillsInitialized: authorizeResponse.IsSkillsInitialized,
    });

    if (ccRefreshToken) {
      this.tokenService.setContactCenterRefreshToken(ccRefreshToken);
    }
  }

  public async signOut(): Promise<void> {
    if (this.accessToken) {
      try {
        await this.authApiService.signOut(this.accessToken);
      } catch {}
    }

    this.internalSignOut();
  }

  private internalSignOut(): void {
    this.clearTokens();
    this.specialistService.removeSpecialist();
    const authSettings = this.configService.authSettings;

    const queryParams = {
      redirect_uri: this.appSignInPageUrl,
      response_type: CognitoConstants.responseType,
      client_id: authSettings.AppClientId,
      state: this.createAuthState(),
      scope: authSettings.OAuthScopes.join(CognitoConstants.scopeDelimiter),
    };

    const logoutRedirectUrl = this.composeCognitoRedirectUrl(CognitoConstants.endpoints.logout, queryParams);

    sessionStorage.clear();
    this.redirectTo(logoutRedirectUrl);
  }

  private clearTokens(): void {
    this.tokenService.removeContactCenterToken();
    sessionStorage.removeItem(authStateItemName);
    sessionStorage.removeItem(authTokenItemName);
    sessionStorage.removeItem(accessTokenItemName);
    sessionStorage.removeItem(refreshTokenItemName);
  }

  private async requestTokens(query: QueryParams): Promise<CognitoAuthResponse> {
    query.client_id = this.configService.authSettings.AppClientId;

    try {
      const response = await this.authApiService.requestToken(query);

      response.status = AuthResult.Success;

      return response;
    } catch (error) {
      console.error(error, `Error occured while getting cognito tokens.`);
      const response = new CognitoAuthResponse();
      response.status = AuthResult.ServerError;
      return response;
    }
  }

  private composeCognitoRedirectUrl(endpointUri: string, queryParams: QueryParams): string {
    let url = this.authApiService.composeCognitoUrl(endpointUri);
    url += `?${stringifyQueryParams(queryParams)}`;
    return url;
  }

  private redirectTo(url: string): void {
    this.window.location.replace(url);
  }

  private createAuthState(): string {
    const newState = Math.floor(Math.random() * 1000000).toString();
    sessionStorage.setItem(authStateItemName, newState);
    return newState;
  }

  private isntNativeMfmPath(requestUrl: string): boolean {
    return AuthService.anonymousPaths.some((path) => requestUrl.includes(path));
  }
}
